/**
 * The DOM layer: top bar, quick-select avatars, the selected villager card (or
 * village stats when nothing is selected), and the event feed. Everything is
 * built once and updated in place so bars animate and the feed can grow.
 */
import type { Clock, HelloMessage, PropKind, TickMessage, VillageEvent, Villager, WorldDiff } from '@village/shared';
import type { ConnStatus } from '../net';
import { Sheet } from './sheet';
import { VillagerCard } from './card';
import { GOAL_LABEL, PHASE_LABEL, el, phaseIcon, timeOf } from './format';

const VILLAGE_NAMES = ['Larkmoor', 'Pinebrook', 'Hollyfen', 'Mossgate', 'Applewick', 'Greyhollow', 'Thistledown', 'Sunnymeade'];

const STATUS_LABEL: Record<ConnStatus, string> = {
  connecting: 'connecting',
  live: 'live',
  reconnecting: 'reconnecting…',
  offline: 'offline',
  mock: 'mock data',
};

interface Stats {
  huts: number;
  trees: number;
  blocks: number;
  villagers: number;
  asleep: number;
}

export class UI {
  private villagers = new Map<string, Villager>();
  private order: string[] = [];
  private selected: string | null = null;
  private lastEventId = 0;
  private card: VillagerCard | null = null;
  private sheet: Sheet;
  private stats: Stats = { huts: 0, trees: 0, blocks: 0, villagers: 0, asleep: 0 };
  private statTiles = new Map<keyof Stats, HTMLElement>();
  private propKinds = new Map<string, PropKind>();
  private hutIds = new Set<string>();

  private $ = <T extends HTMLElement = HTMLElement>(id: string): T => {
    const node = document.getElementById(id);
    if (!node) throw new Error(`missing #${id}`);
    return node as T;
  };

  private villageName = this.$('village-name');
  private phaseIconEl = this.$('phase-icon');
  private phaseLabel = this.$('phase-label');
  private clockDay = this.$('clock-day');
  private clockTime = this.$('clock-time');
  private brainBadge = this.$('brain-badge');
  private countBadge = this.$('villager-count');
  private liveEl = this.$('live');
  private liveLabel = this.$('live-label');
  private avatars = this.$('avatars');
  private cardHost = this.$('villager-card');
  private feed = this.$<HTMLOListElement>('feed');
  private panel = this.$('panel');
  private caption = this.$('stage-caption');

  onSelect: (id: string | null) => void = () => {};

  constructor() {
    this.sheet = new Sheet(this.panel, this.$('sheet-handle'));
    this.phaseIconEl.innerHTML = phaseIcon('day');
    this.renderStats();
  }

  // ------------------------------------------------------------- data in

  setStatus(status: ConnStatus): void {
    this.liveEl.dataset.status = status;
    this.liveLabel.textContent = STATUS_LABEL[status];
    this.liveEl.setAttribute('title', `connection: ${STATUS_LABEL[status]}`);
  }

  setHello(hello: HelloMessage): void {
    const name = VILLAGE_NAMES[Math.abs(hello.world.seed) % VILLAGE_NAMES.length];
    this.villageName.textContent = name;
    this.caption.textContent = `${name} · ${hello.world.size}×${hello.world.size}`;
    this.brainBadge.textContent = hello.brain;
    this.brainBadge.dataset.brain = hello.brain;
    this.brainBadge.title = hello.brain === 'jev' ? 'decisions come from Jev' : 'decisions come from the rules brain';

    this.propKinds = new Map(hello.world.props.map((p) => [p.id, p.kind] as const));
    this.hutIds = new Set(hello.world.structures.filter((s) => s.kind === 'hut').map((s) => s.id));
    this.stats = {
      huts: this.hutIds.size,
      trees: hello.world.props.filter((p) => p.kind === 'tree').length,
      blocks: hello.events.filter((e) => e.type === 'placed').length,
      villagers: hello.villagers.length,
      asleep: hello.villagers.filter((v) => v.asleep).length,
    };

    this.order = hello.villagers.map((v) => v.id);
    this.villagers.clear();
    for (const v of hello.villagers) this.villagers.set(v.id, v);
    this.renderAvatars();
    this.setClock(hello.clock);
    this.feed.replaceChildren();
    this.lastEventId = 0;
    this.pushEvents(hello.events.slice(-40));
    if (this.selected && !this.villagers.has(this.selected)) this.selected = null;
    this.refreshCard(true);
  }

  setTick(tick: TickMessage): void {
    for (const v of tick.villagers) this.villagers.set(v.id, v);
    if (this.order.length !== tick.villagers.length) {
      this.order = tick.villagers.map((v) => v.id);
      this.renderAvatars();
    }
    this.applyDiff(tick.worldDiff, tick.events);
    this.stats.villagers = tick.villagers.length;
    this.stats.asleep = tick.villagers.filter((v) => v.asleep).length;
    this.setClock(tick.clock);
    this.pushEvents(tick.events);
    this.refreshCard(false);
    this.updateAvatars();
  }

  private applyDiff(diff: WorldDiff | undefined, events: VillageEvent[]): void {
    if (diff) {
      // a prop resent with a known id is an hp update, not a new prop
      for (const p of diff.propsAdded ?? []) {
        if (!this.propKinds.has(p.id) && p.kind === 'tree') this.stats.trees++;
        this.propKinds.set(p.id, p.kind);
      }
      for (const id of diff.propsRemoved ?? []) {
        if (this.propKinds.get(id) === 'tree') this.stats.trees = Math.max(0, this.stats.trees - 1);
        this.propKinds.delete(id);
      }
      for (const s of diff.structuresAdded ?? []) {
        if (!this.hutIds.has(s.id) && s.kind === 'hut') {
          this.hutIds.add(s.id);
          this.stats.huts++;
        }
      }
      for (const id of diff.structuresRemoved ?? []) {
        if (this.hutIds.delete(id)) this.stats.huts = Math.max(0, this.stats.huts - 1);
      }
    }
    this.stats.blocks += events.filter((e) => e.type === 'placed').length;
  }

  private setClock(clock: Clock): void {
    this.clockDay.textContent = `Day ${clock.day}`;
    this.clockTime.textContent = timeOf(clock.hour, clock.minute);
    this.phaseLabel.textContent = PHASE_LABEL[clock.phase];
    this.phaseIconEl.innerHTML = phaseIcon(clock.phase);
    this.phaseIconEl.dataset.phase = clock.phase;
    document.body.dataset.phase = clock.phase;
    this.countBadge.textContent = String(this.stats.villagers);
    this.countBadge.title = `${this.stats.villagers} villagers, ${this.stats.asleep} asleep`;
  }

  // ------------------------------------------------------------- selection

  select(id: string | null): void {
    this.selected = id && this.villagers.has(id) ? id : null;
    this.updateAvatars();
    this.refreshCard(true);
    if (this.selected) this.sheet.atLeast('half');
  }

  get selectedId(): string | null {
    return this.selected;
  }

  // ------------------------------------------------------------- avatars

  private renderAvatars(): void {
    this.avatars.replaceChildren();
    for (const id of this.order) {
      const v = this.villagers.get(id);
      if (!v) continue;
      const btn = el('button', 'avatar');
      btn.type = 'button';
      btn.style.setProperty('--c', v.color);
      btn.dataset.villagerId = v.id;
      btn.setAttribute('aria-label', v.name);
      btn.append(el('span', 'avatar-face', v.name.slice(0, 1)), el('span', 'avatar-name', v.name));
      btn.addEventListener('click', () => this.onSelect(v.id));
      this.avatars.append(btn);
    }
    this.updateAvatars();
  }

  private updateAvatars(): void {
    for (const node of Array.from(this.avatars.children) as HTMLElement[]) {
      const id = node.dataset.villagerId!;
      node.classList.toggle('is-selected', id === this.selected);
      const v = this.villagers.get(id);
      if (!v) continue;
      node.title = `${v.name} — ${GOAL_LABEL[v.goal]}${v.asleep ? ' (asleep)' : ''}`;
      node.classList.toggle('is-asleep', v.asleep);
    }
  }

  // ------------------------------------------------------------- card

  private refreshCard(rebuild: boolean): void {
    const v = this.selected ? this.villagers.get(this.selected) : null;
    if (!v) {
      if (this.card || rebuild) {
        this.card = null;
        this.renderStats();
      } else {
        this.updateStats();
      }
      return;
    }
    if (rebuild || !this.card || this.card.villagerId !== v.id) {
      this.card = new VillagerCard(v);
      this.cardHost.replaceChildren(this.card.root);
      this.statTiles.clear();
    } else {
      this.card.update(v);
    }
  }

  /** Nothing selected: what the village has done so far. */
  private renderStats(): void {
    this.cardHost.replaceChildren();
    this.statTiles.clear();
    const wrap = el('div', 'stats');
    wrap.append(el('h2', 'stats-title', 'The village so far'));
    const grid = el('div', 'stat-grid');
    const tiles: Array<[keyof Stats, string]> = [
      ['huts', 'huts built'],
      ['blocks', 'blocks placed'],
      ['trees', 'trees left'],
      ['villagers', 'villagers'],
    ];
    for (const [key, label] of tiles) {
      const tile = el('div', 'stat');
      const value = el('strong', 'stat-value', String(this.stats[key]));
      tile.append(value, el('span', 'stat-label', label));
      grid.append(tile);
      this.statTiles.set(key, value);
    }
    wrap.append(grid);
    wrap.append(el('p', 'stats-hint', 'Tap a villager — or a hut, to meet whoever built it — to watch what the brain decided for them this tick.'));
    this.cardHost.append(wrap);
  }

  private updateStats(): void {
    for (const [key, node] of this.statTiles) node.textContent = String(this.stats[key]);
  }

  // ------------------------------------------------------------- feed

  private pushEvents(events: VillageEvent[]): void {
    const fresh = events.filter((e) => e.id > this.lastEventId);
    if (fresh.length === 0) return;
    this.lastEventId = Math.max(this.lastEventId, ...fresh.map((e) => e.id));
    for (const e of fresh) {
      const v = this.villagers.get(e.villagerId);
      const li = el('li', 'event');
      li.dataset.type = e.type;
      const dot = el('span', 'event-dot');
      dot.style.background = v?.color ?? 'var(--ink-20)';
      const body = el('div', 'event-body');
      body.append(el('p', 'event-text', e.text));
      const where = e.x !== undefined && e.z !== undefined ? ` · ${e.x}, ${e.z}` : '';
      body.append(el('span', 'event-meta', `tick ${e.tick}${where}`));
      li.append(dot, body);
      if (v) {
        li.tabIndex = 0;
        li.classList.add('is-clickable');
        li.addEventListener('click', () => this.onSelect(v.id));
        li.addEventListener('keydown', (ev) => {
          if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault();
            this.onSelect(v.id);
          }
        });
      }
      this.feed.prepend(li);
    }
    while (this.feed.children.length > 80) this.feed.lastElementChild?.remove();
  }
}
